import { useState } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Lock, RefreshCw, Unlock } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { exportToXLSX, buildExportFilename } from '@/utils/exports';
import { paieMoisApi, type BulletinResume } from '@/api/paie';
import { PaieComptaMois } from './PaieComptaMois';

/**
 * PAIE-3 — paie du mois (docs/PLAN-PAIE.md) : préparer (RH, DAF, PDG), contrôler, valider (DAF), rouvrir avec motif,
 * bulletins PDF, livre de paie. « ESSAI — NON VALABLE » tant que les paramètres légaux ne sont pas confirmés.
 */
const moisPrecedent = () => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1); return d.toISOString().slice(0, 7); };
const moisCourant = () => new Date().toISOString().slice(0, 7);
const libelleMois = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const f = (n: number | undefined) => (n == null ? '—' : new Intl.NumberFormat('fr-FR').format(n));

export const PaieBulletins = () => {
  const role = useAuthStore((s) => s.user?.role);
  const filiale = useAuthStore((s) => s.filiale);
  const estDaf = role === 'daf' || role === 'admin_holding';
  const qc = useQueryClient();
  const [mois, setMois] = useState(moisPrecedent());
  const [ouvert, setOuvert] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['paie', 'mois', mois], queryFn: () => paieMoisApi.lire(mois) });
  const maj = (d: unknown) => qc.setQueryData(['paie', 'mois', mois], d);
  const [sansElements, setSansElements] = useState<string[]>([]);

  const preparer = useMutation({ mutationFn: () => paieMoisApi.preparer(mois), onSuccess: (d) => { maj(d); setSansElements(d.sansElements ?? []); toast.success('Paie préparée', libelleMois(mois)); } });
  const valider = useMutation({ mutationFn: () => paieMoisApi.valider(mois), onSuccess: (d) => { maj(d); toast.success('Paie validée', `${libelleMois(mois)} est figée`); } });
  const rouvrir = useMutation({ mutationFn: (motif: string) => paieMoisApi.rouvrir(mois, motif), onSuccess: (d) => { maj(d); toast.success('Paie rouverte'); } });
  const demanderValidation = async () => { if (await demander.confirmer({ titre: `Valider la paie de ${libelleMois(mois)} ?`, message: 'Les bulletins seront numérotés et figés.', confirmer: 'Valider', danger: true })) valider.mutate(); };
  const demanderReouverture = async () => { const m = await demander.texte({ titre: 'Rouvrir la paie', libelle: 'Motif de la réouverture (5 caractères au moins)', min: 5, confirmer: 'Rouvrir', danger: true }); if (m !== null) rouvrir.mutate(m); };
  const pdf = (b: BulletinResume) => paieMoisApi.telechargerPdf(b.id, `bulletin-${mois}-${b.employee.matricule}.pdf`).catch(() => toast.error('PDF indisponible', 'Réessayez.'));

  const d = q.data;
  const p = d?.periode;
  const valide = p?.statut === 'validee';
  const exporter = () => {
    if (!d) return;
    void exportToXLSX<BulletinResume>({
      filename: buildExportFilename(`Livre-de-paie-${mois}`, filiale?.filialeKey, 'xlsx'),
      sheetName: `Paie ${mois}`,
      rows: d.bulletins,
      columns: [
        { header: 'N°', accessor: (b) => b.numero ?? (p?.essai ? 'ESSAI' : 'brouillon') },
        { header: 'Matricule', accessor: (b) => b.employee.matricule },
        { header: 'Salarié', accessor: (b) => b.employee.fullName },
        { header: 'Service', accessor: (b) => b.employee.department },
        { header: 'Brut (F)', accessor: (b) => b.brut ?? '', align: 'right' },
        { header: 'Cotisations salariales (F)', accessor: (b) => b.cotisations ?? '', align: 'right' },
        { header: 'Impôt (F)', accessor: (b) => b.impot ?? '', align: 'right' },
        { header: 'Net à payer (F)', accessor: (b) => b.net ?? '', align: 'right' },
        { header: 'Charges patronales (F)', accessor: (b) => b.chargesPatronales ?? '', align: 'right' },
        { header: 'Points à vérifier', accessor: (b) => b.alertes.join(' · ') },
      ],
    });
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <input type="month" className="ds-fi" style={{ width: 'auto' }} value={mois} max={moisCourant()} onChange={(e) => e.target.value && setMois(e.target.value)} />
        <span style={{ fontSize: 13, textTransform: 'capitalize' }}>{libelleMois(mois)}</span>
        {p ? (valide
          ? <span className="ds-bd ds-bd-s"><Lock size={11} style={{ verticalAlign: -1 }} /> Validée par {p.validePar} le {p.valideLe ? new Date(p.valideLe).toLocaleDateString('fr-FR') : ''}</span>
          : <span className={`ds-bd ${p.essai ? 'ds-bd-d' : 'ds-bd-w'}`}>{p.essai ? 'ESSAI — paramètres non confirmés' : 'Brouillon'}</span>)
          : <span className="ds-bd ds-bd-n">Pas encore préparée</span>}
        <span style={{ flex: 1 }} />
        {!valide && <button className="ds-btn ds-btn-g ds-btn-sm" disabled={preparer.isPending} onClick={() => preparer.mutate()}><RefreshCw size={14} /> {p ? 'Recalculer' : 'Préparer la paie'}</button>}
        {estDaf && p && !valide && mois < moisCourant() && (
          <button className="ds-btn ds-btn-p ds-btn-sm" disabled={valider.isPending || p.essai} title={p.essai ? 'Paramètres légaux à faire confirmer d’abord (onglet Paramètres)' : ''} onClick={demanderValidation}>
            <CheckCircle2 size={14} /> Valider la paie
          </button>
        )}
        {estDaf && valide && <button className="ds-btn ds-btn-g ds-btn-sm" disabled={rouvrir.isPending} onClick={demanderReouverture}><Unlock size={14} /> Rouvrir</button>}
        {d?.montantsVisibles && <button className="ds-btn ds-btn-g ds-btn-sm" disabled={!d.bulletins.length} onClick={exporter}><FileSpreadsheet size={14} /> Livre de paie</button>}
      </div>

      {estDaf && p && <PaieComptaMois mois={mois} periode={p} onChange={() => void q.refetch()} />}

      {p?.essai && (
        <div style={{ background: '#FEE2E2', color: '#7F1D1D', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
          <AlertTriangle size={14} style={{ verticalAlign: -2 }} /> <strong>ESSAI — NON VALABLE.</strong> Les paramètres légaux ne sont pas encore confirmés par un professionnel
          de la paie : ces bulletins servent au contrôle (cabinet), pas au paiement.
        </div>
      )}
      {sansElements.length > 0 && <div style={{ fontSize: 12, color: 'var(--tx-2)' }}>Sans élément de paie (pas de bulletin) : {sansElements.join(', ')}.</div>}
      {!d?.montantsVisibles && d?.periode && <div style={{ fontSize: 12, color: 'var(--tx-2)' }}>Les montants sont réservés au DAF et au PDG.</div>}

      {d?.totaux && (
        <div className="ds-kpi-strip cols-5">
          {[['Effectif', String(d.totaux.effectif)], ['Masse salariale brute', `${f(d.totaux.brut)} F`], ['Net à payer', `${f(d.totaux.net)} F`], ['Charges patronales', `${f(d.totaux.chargesPatronales)} F`], ['Coût employeur', `${f(d.totaux.coutEmployeur)} F`]].map(([t, v]) => (
            <div key={t} className="ds-ks"><div className="ds-ks-label">{t}</div><div className="ds-ks-value" style={{ fontSize: 15 }}>{v}</div></div>
          ))}
        </div>
      )}

      <div className="ds-table-panel">
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="ds-tbl" style={{ minWidth: d?.montantsVisibles ? 860 : 480 }}>
            <thead><tr>
              <th>Salarié</th>
              {d?.montantsVisibles && <><th style={{ textAlign: 'right' }}>Brut</th><th style={{ textAlign: 'right' }}>Cotisations</th><th style={{ textAlign: 'right' }}>Impôt</th><th style={{ textAlign: 'right' }}>Net à payer</th><th style={{ textAlign: 'right' }}>Charges patronales</th></>}
              <th>Points à vérifier</th><th />
            </tr></thead>
            <tbody>
              {q.isLoading && <tr><td colSpan={8}>Chargement…</td></tr>}
              {!q.isLoading && !d?.bulletins.length && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>
                {p ? 'Aucun bulletin' : 'Cliquez sur « Préparer la paie ». Il faut que l’état mensuel du pointage de ce mois soit validé par le DAF.'}
              </td></tr>}
              {d?.bulletins.map((b) => (
                <tr key={b.id}>
                  <td><strong>{b.employee.fullName}</strong><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{b.employee.department} · {b.employee.matricule}{b.numero ? ` · ${b.numero}` : ''}</div></td>
                  {d.montantsVisibles && <>
                    <td style={{ textAlign: 'right' }}>{f(b.brut)}</td><td style={{ textAlign: 'right' }}>{f(b.cotisations)}</td>
                    <td style={{ textAlign: 'right' }}>{f(b.impot)}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{f(b.net)}</td>
                    <td style={{ textAlign: 'right' }}>{f(b.chargesPatronales)}</td>
                  </>}
                  <td style={{ fontSize: 12 }}>
                    {b.nbAlertes === 0 ? <span className="ds-bd ds-bd-s">aucun</span> : (
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setOuvert(ouvert === b.id ? null : b.id)}>{b.nbAlertes} point(s)</button>
                    )}
                    {ouvert === b.id && <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>{b.alertes.map((a) => <li key={a}>{a}</li>)}</ul>}
                  </td>
                  <td>{d.montantsVisibles && <button className="ds-btn ds-btn-g ds-btn-sm" aria-label={`Bulletin de ${b.employee.fullName}`} onClick={() => void pdf(b)}><Download size={12} /> PDF</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
